<template>
  <Teleport to="body">
    <Transition name="sticky-slide">
      <aside
        v-if="isVisible"
        class="landing-pricing-sticky-cta"
        :aria-hidden="isVisible ? 'false' : 'true'"
      >
        <div class="landing-pricing-sticky-cta__inner">
          <KdsButton
            variant="primary"
            :aria-label="isAuthenticated ? t('landing.pricing.stickyCta.ariaLabelLoggedIn') : t('landing.pricing.stickyCta.ariaLabel')"
            @click="onCtaClick"
          >
            {{ isAuthenticated ? t('landing.pricing.stickyCta.ctaLoggedIn') : t('landing.pricing.stickyCta.cta') }}
          </KdsButton>
          <span v-if="!isAuthenticated" class="landing-pricing-sticky-cta__note">
            {{ t('landing.pricing.stickyCta.note') }}
          </span>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsButton from '@/kds/primitives/KdsButton.vue';
import { captureEvent } from '@/boot/posthog';
import { useAuth } from '@/composables/useAuth';

const { t } = useI18n();
const router = useRouter();
const { isAuthenticated } = useAuth();

const isVisible = ref(false);

let pricingObserver: IntersectionObserver | null = null;
let footerObserver: IntersectionObserver | null = null;

interface StickyCtaProps {
  billingCycle?: 'monthly' | 'yearly';
}

const props = withDefaults(defineProps<StickyCtaProps>(), {
  billingCycle: 'monthly'
});

const onCtaClick = (): void => {
  if (isAuthenticated.value) {
    void router.push({ name: 'dashboard' });
    return;
  }
  captureEvent('upgrade_clicked', {
    plan_id: 'studio',
    location: 'landing-pricing-sticky-cta',
    billing_cycle: props.billingCycle
  });
  void router.push({ name: 'auth', query: { mode: 'sign-up' } });
};

onMounted(() => {
  const pricingSection = document.getElementById('pricing');
  const footer = document.querySelector('footer, .kds-footer, [data-footer]');

  if (pricingSection) {
    pricingObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          isVisible.value = entry.isIntersecting || entry.boundingClientRect.top < 0;
        }
      },
      { threshold: 0 }
    );
    pricingObserver.observe(pricingSection);
  }

  if (footer) {
    footerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          isVisible.value = false;
        }
      },
      { threshold: 0 }
    );
    footerObserver.observe(footer);
  }
});

onBeforeUnmount(() => {
  pricingObserver?.disconnect();
  footerObserver?.disconnect();
});
</script>

<style scoped lang="scss">
.landing-pricing-sticky-cta {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--paper);
  border-top: var(--border);
  height: 56px;

  &__inner {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 var(--space-7);
    height: 100%;

    @media (max-width: 639px) {
      flex-direction: column;
      justify-content: center;
      height: auto;
      padding: var(--space-3) var(--space-4);
    }
  }

  &__note {
    font-size: var(--fs-meta);
    color: var(--stone);
    white-space: nowrap;

    @media (max-width: 639px) {
      white-space: normal;
      text-align: center;
    }
  }

  @media (max-width: 639px) {
    height: auto;
  }
}

// Sticky CTA transition
.sticky-slide-enter-active {
  transition: transform 200ms var(--ease-settle), opacity 200ms var(--ease-out);
}

.sticky-slide-leave-active {
  transition: transform 200ms var(--ease-out), opacity 200ms var(--ease-out);
}

.sticky-slide-enter-from,
.sticky-slide-leave-to {
  transform: translateY(56px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .sticky-slide-enter-active,
  .sticky-slide-leave-active {
    transition: none !important;
  }

  .sticky-slide-enter-from,
  .sticky-slide-leave-to {
    transform: none;
    opacity: 1;
  }
}
</style>
